// src/features/account/accountSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { login } from '../../api/account';
import apiClient from '../../api/apiClient';
import { RESET_STATE } from '../../store/resetActions';
import { IS_IDENTITY_BUILD } from '../../config/retailIdentity';
import {
  upsertAccount,
  clearRegistry,
  removeAccountFromRegistry,
  getActiveAccountId,
} from './accountRegistry';

const getInitialAccountState = () => ({
  token: localStorage.getItem('authToken') || null,
  user: (() => {
    const storedUser = localStorage.getItem('authUser');
    return storedUser && storedUser !== 'undefined' ? JSON.parse(storedUser) : null;
  })(),
  loginStatus: 'idle',
  // SPT refused a Google sign-in because sign-up is closed: the login page offers the waitlist (DWLF-584).
  signUpClosed: false,
  error: null,
  logoutReason: null,
});

// Thunk for handling the login API call
export const loginUser = createAsyncThunk(
  'account/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await login(credentials); // API call to backend login
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ✅ New: Thunk for Google Login
export const googleLoginUser = createAsyncThunk(
  'account/googleLogin',
  async (googleToken, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/accounts/google-login', { token: googleToken });
      return response.data;
    } catch (error) {
      // The status travels with the body: a 403 means SPT refused a new person (sign-up is closed).
      return rejectWithValue({ ...error.response?.data, status: error.response?.status });
    }
  }
);

const accountSlice = createSlice({
  name: 'account',
  initialState: getInitialAccountState(),
  reducers: {
    /** The login page is opened afresh: a refusal from an earlier visit is not this visit's. */
    clearSignUpClosed: (state) => {
      state.signUpClosed = false;
    },
    logout: (state, action) => {
      const reason = action?.payload?.reason || 'manual';
      state.token = null;
      state.user = null;
      state.logoutReason = reason;
      // Reconcile the multi-account registry before clearing the active session.
      //  - 'expired' (involuntary, dispatched by apiClient when a refresh fails):
      //    evict ONLY the account whose session expired. Other added accounts are
      //    preserved in the registry, so they reappear in the switcher after the
      //    next sign-in rather than being forgotten. (Auto-switching straight to
      //    a remaining valid account on expiry is a possible future enhancement —
      //    it'd need to live in apiClient's interceptor, not this pure reducer.)
      //  - 'manual' (the user chose to sign out): drop the whole registry so no
      //    stale refresh tokens linger and no ghost accounts reappear next login.
      // (Granular "switch to another account" sign-out goes through
      // signOutActiveAccount(), not this reducer.)
      if (reason === 'expired') {
        removeAccountFromRegistry(getActiveAccountId()); // read before clearing authUser
      } else {
        clearRegistry();
      }
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      localStorage.removeItem('refreshToken');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loginStatus = 'loading';
        state.signUpClosed = false;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loginStatus = 'succeeded';
        console.log('loginUser.fulfilled', action.payload);
        const token = action.payload.accessToken || action.payload.token;
        state.token = token;
        state.user = action.payload.user;
        state.logoutReason = null;
        localStorage.setItem('authToken', token);
        localStorage.setItem('authUser', JSON.stringify(action.payload.user));
        if (action.payload.refreshToken) {
          localStorage.setItem('refreshToken', action.payload.refreshToken);
        }
        // Remember this account in the multi-account registry so it can be
        // switched to later (and so an "add account" login keeps the others).
        upsertAccount({ user: action.payload.user, token, refreshToken: action.payload.refreshToken });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loginStatus = 'failed';
        state.error = action.payload.error || 'Failed to login';
      })

      // ✅ Handle Google Login
      .addCase(googleLoginUser.pending, (state) => {
        state.loginStatus = 'loading';
        state.signUpClosed = false;
      })
      .addCase(googleLoginUser.fulfilled, (state, action) => {
        state.loginStatus = 'succeeded';
        const token = action.payload.accessToken || action.payload.token;
        state.token = token;
        state.user = action.payload.user;
        state.logoutReason = null;
        localStorage.setItem('authToken', token);
        localStorage.setItem('authUser', JSON.stringify(action.payload.user));
        if (action.payload.refreshToken) {
          localStorage.setItem('refreshToken', action.payload.refreshToken);
        }
        upsertAccount({ user: action.payload.user, token, refreshToken: action.payload.refreshToken });
      })
      .addCase(googleLoginUser.rejected, (state, action) => {
        state.loginStatus = 'failed';
        state.error = action.payload.error || 'Google login failed';
        // SPT's only 403 here: a Google account with no DWLF account, while sign-up is closed. Only
        // the legacy build offers the waitlist for it: in the identity build the person reaching
        // this sign-in already holds a retail invite, so the missing SPT account is shown as it is.
        state.signUpClosed = !IS_IDENTITY_BUILD && action.payload.status === 403;
      })

      .addCase(RESET_STATE, (state, action) => {
        const baseState = getInitialAccountState();
        return {
          ...baseState,
          logoutReason: action?.payload?.logoutReason ?? baseState.logoutReason,
        };
      });
  },
});

export const { logout, clearSignUpClosed } = accountSlice.actions;
export default accountSlice.reducer;
